import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Users, FileText, Building2, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { StatusBadge } from '@/components/ui/Badge'

interface SearchResult {
  id: string
  type: 'candidate' | 'mandate' | 'partner'
  name: string
  subtitle: string
  status?: string
}

interface GlobalSearchProps {
  open: boolean
  onClose: () => void
}

const SECTION_LABELS: Record<string, string> = {
  candidate: 'CANDIDATS',
  mandate:   'MANDATS',
  partner:   'PARTENAIRES',
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
  candidate: <Users size={14} className="text-indigo-500" />,
  mandate:   <FileText size={14} className="text-purple-500" />,
  partner:   <Building2 size={14} className="text-emerald-500" />,
}

export default function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([])
      setActiveIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Global keyboard shortcut CMD+K / CTRL+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (!open) onClose() // parent toggles
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    const term = `%${q}%`

    const [candidatesRes, mandatesRes, partnersRes] = await Promise.all([
      supabase
        .from('candidates')
        .select('id, first_name, last_name, title, status')
        .or(`first_name.ilike.${term},last_name.ilike.${term},title.ilike.${term}`)
        .limit(5),
      supabase
        .from('mandates')
        .select('id, ref, client_name, position, status')
        .or(`client_name.ilike.${term},position.ilike.${term},ref.ilike.${term}`)
        .limit(5),
      supabase
        .from('partners')
        .select('id, name, company')
        .or(`name.ilike.${term},company.ilike.${term}`)
        .eq('active', true)
        .limit(5),
    ])

    const candidates: SearchResult[] = (candidatesRes.data ?? []).map((c: any) => ({
      id: c.id,
      type: 'candidate',
      name: `${c.first_name} ${c.last_name}`,
      subtitle: c.title ?? '',
      status: c.status,
    }))

    const mandates: SearchResult[] = (mandatesRes.data ?? []).map((m: any) => ({
      id: m.id,
      type: 'mandate',
      name: `${m.ref} — ${m.position}`,
      subtitle: m.client_name,
      status: m.status,
    }))

    const partners: SearchResult[] = (partnersRes.data ?? []).map((p: any) => ({
      id: p.id,
      type: 'partner',
      name: p.name,
      subtitle: p.company ?? '',
    }))

    setResults([...candidates, ...mandates, ...partners])
    setActiveIndex(0)
    setLoading(false)
  }, [])

  // Debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(query), 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, search])

  function handleSelect(result: SearchResult) {
    onClose()
    if (result.type === 'candidate') navigate('/app/vivier')
    else if (result.type === 'mandate') navigate('/app/mandats')
    else if (result.type === 'partner') navigate('/app/partenaires')
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && results[activeIndex]) {
      handleSelect(results[activeIndex])
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  // Group results by type
  const groups = (['candidate', 'mandate', 'partner'] as const).map((type) => ({
    type,
    items: results.filter((r) => r.type === type),
  })).filter((g) => g.items.length > 0)

  // Flat index for keyboard nav
  let flatIndex = 0

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -8 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-slate-700">
              <Search size={18} className="text-gray-400 dark:text-slate-500 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Rechercher un candidat, mandat, partenaire…"
                className="flex-1 text-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 bg-transparent outline-none"
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600 transition">
                  <X size={16} />
                </button>
              )}
              <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-500 dark:text-gray-400 font-mono">
                Esc
              </kbd>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {loading && (
                <div className="flex justify-center py-6">
                  <div className="w-5 h-5 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {!loading && query.length >= 2 && results.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">
                  Aucun résultat pour "{query}"
                </p>
              )}

              {!loading && query.length < 2 && (
                <p className="text-xs text-gray-400 text-center py-6">
                  Tapez au moins 2 caractères pour rechercher…
                </p>
              )}

              {!loading && groups.map((group) => (
                <div key={group.type}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-4 pt-4 pb-1">
                    {SECTION_LABELS[group.type]}
                  </p>
                  {group.items.map((result) => {
                    const idx = flatIndex++
                    const isActive = idx === activeIndex
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition ${
                          isActive ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'
                        }`}
                      >
                        <div className="shrink-0">{SECTION_ICONS[result.type]}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">{result.name}</p>
                          {result.subtitle && (
                            <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{result.subtitle}</p>
                          )}
                        </div>
                        {result.status && (
                          <div className="shrink-0">
                            <StatusBadge status={result.status as any} />
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              ))}

              {groups.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-50 dark:border-slate-700">
                  <p className="text-[10px] text-gray-400 dark:text-slate-500">
                    ↑↓ naviguer &nbsp;·&nbsp; Entrée pour ouvrir &nbsp;·&nbsp; Échap pour fermer
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
